import { forwardRef } from 'react';
import { cn } from '@/shared/lib/utils';
import type { FamilyMember } from '@/shared/types/member.types';
import { Avatar, AvatarFallback, AvatarImage } from './avatar';

/**
 * MemberAvatar sizes
 */
export type MemberAvatarSize = 'sm' | 'md' | 'lg';

/**
 * Size configurations for MemberAvatar
 */
const AVATAR_SIZES: Readonly<
  Record<MemberAvatarSize, { avatar: string; text: string; name: string }>
> = {
  sm: { avatar: 'size-6', text: 'text-xs', name: 'text-xs' },
  md: { avatar: 'size-8', text: 'text-sm', name: 'text-sm' },
  lg: { avatar: 'size-10', text: 'text-base', name: 'text-base' },
} as const;

/**
 * Props for MemberAvatar component
 */
export interface MemberAvatarProps {
  /** Family member to display - only requires id, displayName, and optional avatar */
  readonly member: Pick<FamilyMember, 'id' | 'displayName' | 'avatar'>;
  /** Size of the avatar */
  readonly size?: MemberAvatarSize;
  /** Whether to show the member's name next to the avatar */
  readonly showName?: boolean;
  /** Additional CSS classes */
  readonly className?: string;
}

/**
 * Get initials from display name
 * Takes first letter of first and last word
 */
function getInitials(displayName: string): string {
  const words = displayName.trim().split(/\s+/);
  if (words.length === 1) {
    return words[0]?.charAt(0).toUpperCase() ?? '?';
  }
  const first = words[0]?.charAt(0) ?? '';
  const last = words[words.length - 1]?.charAt(0) ?? '';
  return (first + last).toUpperCase();
}

/**
 * MemberAvatar - Display family member avatar with optional name
 *
 * Uses the existing Avatar component internally with proper fallback
 * to initials when no avatar image is available.
 *
 * @example
 * ```tsx
 * <MemberAvatar member={member} size="md" />
 * <MemberAvatar member={member} showName />
 * ```
 */
export const MemberAvatar = forwardRef<HTMLDivElement, MemberAvatarProps>(
  ({ member, size = 'md', showName = false, className }, ref) => {
    const sizeConfig = AVATAR_SIZES[size];
    const initials = getInitials(member.displayName);

    return (
      <div
        ref={ref}
        className={cn('flex items-center gap-2', className)}
        data-member-id={member.id}
      >
        <Avatar className={sizeConfig.avatar}>
          {member.avatar && (
            <AvatarImage
              src={member.avatar}
              alt={`${member.displayName} аватар`}
            />
          )}
          <AvatarFallback className={sizeConfig.text}>
            {initials}
          </AvatarFallback>
        </Avatar>

        {showName && (
          <span
            className={cn(
              'truncate font-medium text-foreground',
              sizeConfig.name
            )}
          >
            {member.displayName}
          </span>
        )}
      </div>
    );
  }
);

MemberAvatar.displayName = 'MemberAvatar';
